import type {
  AppState,
  BinaryFiles,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";

export const EMBED_HOST_SOURCE = "excalidraw-embed-host" as const;
export const EMBED_SOURCE = "excalidraw-embed" as const;

export type EmbedTheme = "light" | "dark";
export type EmbedLangCode = "en" | "zh-CN";

export type EmbedOptions = {
  langCode?: EmbedLangCode;
  theme?: EmbedTheme;
  viewModeEnabled?: boolean;
  gridModeEnabled?: boolean;
  initialJson: ExcalidrawInitialDataState | null;
};

export type EmbedHostMessage =
  | {
      source: typeof EMBED_HOST_SOURCE;
      type: "load";
      requestId?: string;
      payload?: string | ExcalidrawInitialDataState | null;
    }
  | {
      source: typeof EMBED_HOST_SOURCE;
      type:
        | "getJson"
        | "exportPng"
        | "exportSvg"
        | "downloadPng"
        | "downloadSvg"
        | "downloadSource";
      requestId?: string;
      payload?: unknown;
    }
  | {
      source: typeof EMBED_HOST_SOURCE;
      type: "setLang";
      requestId?: string;
      payload?: EmbedLangCode;
    }
  | {
      source: typeof EMBED_HOST_SOURCE;
      type: "setTheme";
      requestId?: string;
      payload?: EmbedTheme;
    };

export type EmbedEventType = "ready" | "change" | "response" | "error";

export type EmbedEvent<TPayload = unknown> = {
  source: typeof EMBED_SOURCE;
  type: EmbedEventType;
  requestId?: string;
  payload?: TPayload;
};

const truthyValues = new Set(["1", "true", "yes"]);
const falseyValues = new Set(["0", "false", "no"]);

const parseBooleanParam = (value: string | null) => {
  if (!value) {
    return undefined;
  }
  if (truthyValues.has(value)) {
    return true;
  }
  if (falseyValues.has(value)) {
    return false;
  }
  return undefined;
};

export const parseInitialJson = (
  value: string | ExcalidrawInitialDataState | null | undefined,
): ExcalidrawInitialDataState | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("Invalid Excalidraw embed JSON");
    }
    return parsed as ExcalidrawInitialDataState;
  }

  return JSON.parse(JSON.stringify(value)) as ExcalidrawInitialDataState;
};

export const parseEmbedOptions = (url: URL): EmbedOptions => {
  const lang = url.searchParams.get("lang");
  const theme = url.searchParams.get("theme");

  return {
    langCode: lang === "en" || lang === "zh-CN" ? lang : undefined,
    theme: theme === "light" || theme === "dark" ? theme : undefined,
    viewModeEnabled: parseBooleanParam(url.searchParams.get("viewMode")),
    gridModeEnabled: parseBooleanParam(url.searchParams.get("gridMode")),
    initialJson: parseInitialJson(url.searchParams.get("json")),
  };
};

export const isEmbedHostMessage = (
  value: unknown,
): value is EmbedHostMessage => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const message = value as { source?: unknown; type?: unknown };
  return (
    message.source === EMBED_HOST_SOURCE &&
    typeof message.type === "string" &&
    [
      "load",
      "getJson",
      "exportPng",
      "exportSvg",
      "downloadPng",
      "downloadSvg",
      "downloadSource",
      "setLang",
      "setTheme",
    ].includes(message.type)
  );
};

export const createEmbedEvent = <TPayload>(
  type: EmbedEventType,
  payload?: TPayload,
  requestId?: string,
): EmbedEvent<TPayload> => ({
  source: EMBED_SOURCE,
  type,
  ...(requestId ? { requestId } : null),
  ...(typeof payload === "undefined" ? null : { payload }),
});

export const serializeEmbedScene = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
) =>
  serializeAsJSON(elements, appState, files, "local");

export const blobToDataURL = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result)));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });
